import moment, { Moment } from 'moment';
import { BASE_SCHEMA } from '@/shared/converters/baseConverters';
import User from '@/shared/models/User';
import { isPresent } from '@/shared/utils/typescript';
import DiagnosticBoolean from '../constants/DiagnosticBoolean';
import DiagnosticType from '../constants/DiagnosticType';
import {
  AdviceDiagnosticData,
  BooleanDiagnosticData,
  CommentDiagnosticData,
  ComplaintDiagnosticData,
  DiagnosisDiagnosticData,
  DiagnosticData,
  ObservationDiagnosticData,
  PartsToStockDiagnosticData,
  RepairDiagnosticData,
  ResultDiagnosticData,
  ReviewDiagnosticData,
} from '../models/diagnosis/DiagnosticData';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { formatMoment, parseMoment } from '@/shared/converters/dateConverters';
import { minutesSinceMidnight } from '@/shared/utils/dateTime';
import Diagnostic from '../models/diagnosis/Diagnostic';
import DiagnosticTypeDescription from '../models/diagnosis/DiagnosticTypeDescription';

const SCHEMA = {
  ...BASE_SCHEMA,
  typeDescriptionId: 'stepId',
  description: 'description',
  hasTimer: 'hasTimer',
  hasComment: 'hasComment',
  hasMediaEnabled: 'hasMediaEnabled',
  isMandatory: 'isMandatory',
  isAdditionalTime: 'isAdditionalTime',
  isExportable: 'isExportable',
  createdBy: (data: any) => User.withId(data.createdBy ?? data.createdById),
  answeredBy: (data: any) => User.withId(data.answeredBy),
  answeredOn: (data: any) => parseMoment(data.answeredOn),
  startedOn: (data: any) => parseMoment(data.startedOn),
  stoppedOn: (data: any) => parseMoment(data.stoppedOn),
  comment: 'comment',
};

const DIAGNOSTIC_TYPE_SCHEMA = {
  type: 'code',
  description: 'description',
  isActive: 'isActive',
  hasTimer: 'hasTimer',
  hasComment: 'hasComment',
};

const COMMENT_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
};

const COMPLAINT_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  complaint: 'userInputString1',
  conditions: 'userInputString2',
};

const OBSERVATION_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  isObserved: (data: any) => data.userInputCheck1 === DiagnosticBoolean.TRUE,
};

const DIAGNOSIS_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  expected: 'userInputString1',
  actual: 'userInputString2',
};

const ADVICE_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  estimatedTime: (data: any) => parseEstimatedTime(data.userInputString1),
  isNeeded: (data: any) => isPresent(data.userInputCheck1) ? data.userInputCheck1 === DiagnosticBoolean.TRUE : null,
  mattersToInspection: (data: any) => isPresent(data.userInputCheck2) ? data.userInputCheck2 === DiagnosticBoolean.TRUE : null,
};

const REPAIR_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
};

const RESULT_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  isResolved: (data: any) => isPresent(data.userInputCheck1) ? data.userInputCheck1 === DiagnosticBoolean.TRUE : null,
};

const PARTS_TO_STOCK_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  isWarranty: (data: any) => isPresent(data.userInputCheck1) ? data.userInputCheck1 === DiagnosticBoolean.TRUE : null,
};

const REVIEW_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  isComplete: (data: any) => isPresent(data.userInputCheck1) ? data.userInputCheck1 === DiagnosticBoolean.TRUE : null,
};

const BOOLEAN_DIAGNOSTIC_SCHEMA = {
  ...SCHEMA,
  isTrue: (data: any) => isPresent(data.userInputCheck1) ? data.userInputCheck1 === DiagnosticBoolean.TRUE : null,
};

function parseEstimatedTime(data: any): Moment | null {
  if (data === null) return null;
  return moment().startOf('day').add(parseInt(data, 10), 'minutes');
}

export function parseDiagnostics(data: any): Array<Diagnostic> {
  return parseEntities(data, parseDiagnostic);
}

export function parseDiagnostic(data: any): Diagnostic | null {
  return parseEntity(data, getDiagnosticSchema(data), Diagnostic.getSubclass(data.code));
}

function getDiagnosticSchema(data: any): any {
  switch (data.code) {
    case DiagnosticType.COMMENT:
    case DiagnosticType.TEXT:
      return COMMENT_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.COMPLAINT:
      return COMPLAINT_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.OBSERVATION:
      return OBSERVATION_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.DIAGNOSIS:
      return DIAGNOSIS_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.ADVICE:
      return ADVICE_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.REPAIR:
      return REPAIR_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.RESULT:
      return RESULT_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.PARTS_TO_STOCK:
      return PARTS_TO_STOCK_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.REVIEW:
      return REVIEW_DIAGNOSTIC_SCHEMA;
    case DiagnosticType.BOOLEAN:
      return BOOLEAN_DIAGNOSTIC_SCHEMA;
  }
}

export function parseDiagnosticType(data: any): Array<DiagnosticTypeDescription> {
  return parseEntities(data, DIAGNOSTIC_TYPE_SCHEMA, DiagnosticTypeDescription);
}

export function formatDiagnostic(diagnostic: Diagnostic, data: DiagnosticData): any {
  return {
    stepId: diagnostic.typeDescriptionId,
    comment: null,
    inputString1: null,
    inputString2: null,
    inputCheck1: null,
    inputCheck2: null,
    timerStarted: formatMoment(diagnostic.startedOn),
    isAdditionalTime: false,
    linkedMedia: [],
    ...formatDiagnosticContent(data, diagnostic.type),
  };
}

export function formatDiagnosticContent(data: DiagnosticData, type: DiagnosticType): any {
  switch (type) {
    case DiagnosticType.COMMENT:
    case DiagnosticType.TEXT:
      return formatCommentDiagnosticData(data as CommentDiagnosticData);
    case DiagnosticType.COMPLAINT:
      return formatComplaintDiagnosticData(data as ComplaintDiagnosticData);
    case DiagnosticType.OBSERVATION:
      return formatObservationDiagnosticData(data as ObservationDiagnosticData);
    case DiagnosticType.DIAGNOSIS:
      return formatDiagnosisDiagnosticData(data as DiagnosisDiagnosticData);
    case DiagnosticType.ADVICE:
      return formatAdviceDiagnosticData(data as AdviceDiagnosticData);
    case DiagnosticType.REPAIR:
      return formatRepairDiagnosticData(data as RepairDiagnosticData);
    case DiagnosticType.RESULT:
      return formatResultDiagnosticData(data as ResultDiagnosticData);
    case DiagnosticType.PARTS_TO_STOCK:
      return formatPartsToStockDiagnosticData(data as PartsToStockDiagnosticData);
    case DiagnosticType.REVIEW:
      return formatReviewDiagnosticData(data as ReviewDiagnosticData);
    case DiagnosticType.BOOLEAN:
      return formatBooleanDiagnosticData(data as BooleanDiagnosticData);
  }
}

function formatCommentDiagnosticData(data: CommentDiagnosticData): any {
  return {
    comment: data.comment,
  };
}

function formatComplaintDiagnosticData(data: ComplaintDiagnosticData): any {
  return {
    inputString1: data.complaint,
    inputString2: data.conditions,
  };
}

function formatObservationDiagnosticData(data: ObservationDiagnosticData): any {
  return {
    comment: data.comment,
    inputCheck1: data.isObserved ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
  };
}

function formatDiagnosisDiagnosticData(data: DiagnosisDiagnosticData): any {
  return {
    comment: data.comment,
    inputString1: data.expected,
    inputString2: data.actual,
    isAdditionalTime: data.isAdditionalTime,
  };
}

function formatAdviceDiagnosticData(data: AdviceDiagnosticData): any {
  return {
    comment: data.comment,
    inputString1: data.estimatedTime ? `${minutesSinceMidnight(data.estimatedTime)}` : null,
    inputCheck1: data.isNeeded ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
    inputCheck2: data.mattersToInspection ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
  };
}

function formatRepairDiagnosticData(data: RepairDiagnosticData): any {
  return {
    comment: data.comment,
    isAdditionalTime: data.isAdditionalTime,
  };
}

function formatResultDiagnosticData(data: ResultDiagnosticData): any {
  return {
    comment: data.comment,
    inputCheck1: data.isResolved ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
  };
}

function formatPartsToStockDiagnosticData(data: PartsToStockDiagnosticData): any {
  return {
    comment: data.comment,
    inputCheck1: data.isWarranty ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
  };
}

function formatReviewDiagnosticData(data: ReviewDiagnosticData): any {
  return {
    comment: data.comment,
    inputCheck1: data.isComplete ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
  };
}

function formatBooleanDiagnosticData(data: BooleanDiagnosticData): any {
  return {
    inputCheck1: data.isTrue ? DiagnosticBoolean.TRUE : DiagnosticBoolean.FALSE,
    comment: data.comment,
  };
}